import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { differenceInHours } from 'date-fns';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { OptionalExchangeRatesCurrentRequest } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type { AuctionReport } from 'src/apis/monolith/types';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { FormatValue } from 'src/components/data-display/format-value';
import { Td, Th } from 'src/components/data-display/table';
import { Tooltip } from 'src/components/overlay/tooltip';
import { convertPriceToEURMWhValue } from 'src/pages/transport/auctions/details/helper';

export const PriceInEURMWhTh: FC = () => {
  return (
    <Th>
      In EUR/MWh{' '}
      <Tooltip
        content={
          <small>
            The value presented in EUR/MWh is indicative, representing the
            pro-rated capacity charge. This calculation assumes full utilization
            of the capacity throughout the entire runtime and incorporates the
            starting price reflecting the regulated tariff as uploaded by the
            TSOs.
          </small>
        }
      >
        {(targetProps) => (
          <FontAwesomeIcon icon={faInfoCircle} {...targetProps} />
        )}
      </Tooltip>
    </Th>
  );
};

export const PriceInEURMWhTd: FC<{
  auction: AuctionReport;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
}> = ({ auction, exchangeRates }) => {
  const startingPriceToEURMWh = useMemo(() => {
    const hours = differenceInHours(
      new Date(auction.runtime.end),
      new Date(auction.runtime.start)
    );
    try {
      return convertPriceToEURMWhValue({
        price: auction.startingPrice,
        exchangeRates,
        hours,
      });
    } catch (error) {
      // we could not convert the price
      return null;
    }
  }, [auction, exchangeRates]);

  return (
    <Td data-testid={`startingPriceEURMWh-${auction.id}`} align="right">
      {startingPriceToEURMWh ? (
        <FormatValue
          value={startingPriceToEURMWh}
          label="EUR/MWh"
          type="4-decimals"
          smallLabel
          stacked
        />
      ) : (
        <EmptyValue label="Exchange Rate Unavailable" />
      )}
    </Td>
  );
};
